import { styled } from '@linaria/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  inkDark,
  inkDarkest,
  inkLight,
  primaryBase,
  secondaryBase,
  secondaryDarkest,
  secondaryLightest,
  skyDark,
  skyLight,
  skyLighter,
  yellowDarkest,
} from '../../../../styles/design-tokens';
import { fmtNum } from '../../shared/formatters';
import { Additions, Deletions, LegendItem, LegendRow, LegendSwatch, NotTraced } from './pr-detail.helpers';
import { baseNameOf, fileKeptPct, fileSurvivalSegments, groupFilesByDirectory } from './pr-survival.helpers';
import type { PullRequestFile } from '../../../../api/agent-miner-client/agent-miner-client.type';

type PrFileTreeProps = {
  files: PullRequestFile[];
  selectedFileId: string | null;
  onSelect: (fileId: string) => void;
};

/**
 * GitHub-style file list for the side panel: files grouped under collapsible
 * directories, each row showing the basename, +/-, the share of the agent's
 * lines that were kept, and a kept / not merged / human bar.
 *
 * @param {PrFileTreeProps} props - Files, the selected file id, and the selection callback
 * @returns {JSX.Element} The file tree
 */
export function PrFileTree({ files, selectedFileId, onSelect }: PrFileTreeProps) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const groups = groupFilesByDirectory(files);

  const toggle = (directory: string) =>
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(directory)) next.delete(directory);
      else next.add(directory);
      return next;
    });

  return (
    <Tree>
      <LegendRow>
        <LegendItem>
          <LegendSwatch $color={primaryBase} />
          {t('agent_miner.pull_requests.files.legend_kept')}
        </LegendItem>
        <LegendItem>
          <LegendSwatch $color={yellowDarkest} />
          {t('agent_miner.pull_requests.files.legend_not_merged')}
        </LegendItem>
        <LegendItem>
          <LegendSwatch $color={secondaryBase} />
          {t('agent_miner.pull_requests.files.legend_other_agents')}
        </LegendItem>
        <LegendItem>
          <LegendSwatch $color={skyDark} />
          {t('agent_miner.pull_requests.files.legend_human')}
        </LegendItem>
      </LegendRow>
      {groups.map((group) => {
        const open = !collapsed.has(group.directory);
        return (
          <DirectoryGroup key={group.directory || '__root__'}>
            <DirectoryButton type="button" aria-expanded={open} $open={open} onClick={() => toggle(group.directory)}>
              {group.directory || t('agent_miner.pull_requests.panel.root_directory')}
            </DirectoryButton>
            {open &&
              group.files.map((file) => {
                const selected = file.change_request_file_id === selectedFileId;
                // A missing flag defaults to precise so legacy/cached payloads keep
                // the current rendering; only an explicit false is non-precise.
                const precise = file.line_precise !== false;
                const keptPct = fileKeptPct(file);
                const segments = fileSurvivalSegments(file);
                const total = segments.kept + segments.notMerged + segments.agentOther + segments.human;
                const width = (value: number) => (total > 0 ? `${(value / total) * 100}%` : '0%');
                const summary = !precise
                  ? t('agent_miner.pull_requests.files.not_line_traced')
                  : file.authored > 0
                    ? t('agent_miner.pull_requests.files.summary', {
                        kept: fmtNum(segments.kept),
                        not_merged: fmtNum(segments.notMerged),
                        other_agents: fmtNum(segments.agentOther),
                        human: fmtNum(segments.human),
                      })
                    : t('agent_miner.pull_requests.files.no_agent_lines');
                return (
                  <FileButton
                    key={file.change_request_file_id}
                    type="button"
                    aria-current={selected ? 'true' : undefined}
                    $selected={selected}
                    title={summary}
                    onClick={() => onSelect(file.change_request_file_id)}
                  >
                    <FileTop>
                      <FileLabel>{baseNameOf(file.filename)}</FileLabel>
                      {precise ? keptPct !== null && <KeptPct>{keptPct}%</KeptPct> : <MutedValue>—</MutedValue>}
                    </FileTop>
                    {precise ? (
                      <Bar aria-hidden="true">
                        <Segment $width={width(segments.kept)} $color={primaryBase} />
                        <Segment $width={width(segments.notMerged)} $color={yellowDarkest} />
                        <Segment $width={width(segments.agentOther)} $color={secondaryBase} />
                        <Segment $width={width(segments.human)} $color={skyDark} />
                      </Bar>
                    ) : (
                      <NotTraced>{t('agent_miner.pull_requests.files.not_line_traced')}</NotTraced>
                    )}
                    <FileStat>
                      <Additions>+{fmtNum(file.additions)}</Additions> <Deletions>−{fmtNum(file.deletions)}</Deletions>
                    </FileStat>
                  </FileButton>
                );
              })}
          </DirectoryGroup>
        );
      })}
    </Tree>
  );
}

/**
 * Rotation for a directory group's disclosure arrow.
 * @param {boolean} open - Whether the group is expanded.
 * @returns {string} A CSS transform value.
 */
const directoryArrowTransform = (open: boolean): string => (open ? 'rotate(90deg)' : 'none');

/**
 * Border colour for a file row: highlighted when selected.
 * @param {boolean} selected - Whether the row is the selected file.
 * @returns {string} A design-token colour.
 */
const fileBorderColor = (selected: boolean): string => (selected ? secondaryBase : skyLight);

/**
 * Background colour for a file row: tinted when selected.
 * @param {boolean} selected - Whether the row is the selected file.
 * @returns {string} A design-token colour.
 */
const fileBackground = (selected: boolean): string => (selected ? secondaryLightest : skyLighter);

const Tree = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DirectoryGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DirectoryButton = styled.button<{ $open: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  background: none;
  border: none;
  padding: 4px 2px;
  text-align: left;
  font: 600 11px/1.2 'SF Mono', ui-monospace, Menlo, monospace;
  color: ${inkLight};
  cursor: pointer;

  &::before {
    content: '▶';
    font-size: 8px;
    display: inline-block;
    transform: ${(props) => directoryArrowTransform(props.$open)};
    transition: transform 0.12s ease;
  }
`;

const FileButton = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
  margin-left: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${(props) => fileBorderColor(props.$selected)};
  background: ${(props) => fileBackground(props.$selected)};
  text-align: left;
  cursor: pointer;
  box-sizing: border-box;
  max-width: calc(100% - 10px);

  &:hover {
    border-color: ${secondaryBase};
  }

  &:focus-visible {
    outline: 2px solid ${secondaryBase};
    outline-offset: 2px;
  }
`;

const FileTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-width: 0;
`;

const FileLabel = styled.span`
  font: 600 11.5px/1.2 'SF Mono', ui-monospace, Menlo, monospace;
  color: ${inkDarkest};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const KeptPct = styled.span`
  flex-shrink: 0;
  font: 600 10.5px/1 'Figtree', sans-serif;
  color: ${secondaryDarkest};
  font-variant-numeric: tabular-nums;
`;

const MutedValue = styled.span`
  flex-shrink: 0;
  font: 600 10.5px/1 'Figtree', sans-serif;
  color: ${skyDark};
`;

const Bar = styled.div`
  display: flex;
  width: 100%;
  height: 5px;
  border-radius: 3px;
  overflow: hidden;
  background: ${skyLight};
`;

const Segment = styled.div<{ $width: string; $color: string }>`
  height: 100%;
  width: ${(props) => props.$width};
  background: ${(props) => props.$color};
`;

const FileStat = styled.span`
  font-size: 10px;
  color: ${inkDark};
`;
